import React, { useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Badge,
  Icon,
  Link,
  Spinner,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { FaArrowLeft, FaPen, FaPause, FaPlay, FaBolt, FaFilePdf, FaFileWord, FaRegClock, FaDownload, FaRegNewspaper, FaSitemap, FaCheck, FaExclamationTriangle } from "react-icons/fa";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import IconBox from "components/Icons/IconBox";
import { statut, canal, tonalite, fmtNombre } from "variables/veille";
import { useVeilles, fetchMentions, fetchReports, downloadReport, fetchMatriceJob, requestMatrice, downloadMatrice, saveMatrice, requestRerender, requestRun, fetchRunJob } from "contexts/VeillesContext";
import { useSession } from "contexts/SessionContext";
import { apercuUrlVeille, telechargerDocVeille } from "utils/veillePdf";
import MatriceEditor from "./MatriceEditor";

// Phases du job de génération de matrice, dans l'ordre d'exécution du moteur
const PHASES_MATRICE = [
  { id: "en_attente", label: "En file d'attente" },
  { id: "cadrage", label: "Cadrage de la demande" },
  { id: "recherche_dossiers", label: "Recherche des dossiers prioritaires (web)" },
  { id: "organigrammes", label: "Organigrammes & personnes clés (web)" },
  { id: "derivation", label: "Mots-clés & tags" },
  { id: "assemblage", label: "Assemblage du document" },
];
const JOB_ACTIF = (s) => s && s !== "termine" && s !== "echec";

// Phases d'une exécution de veille (« Lancer maintenant »)
const PHASES_RUN = [
  { id: "collecte", label: "Collecte des retombées" },
  { id: "rapport", label: "Rédaction du rapport" },
];

const fmtPeriode = (r) => {
  if (!r.periode_debut || !r.periode_fin) return "";
  const opts = { day: "numeric", month: "short" };
  const d = new Date(r.periode_debut).toLocaleDateString("fr-FR", opts);
  const f = new Date(r.periode_fin).toLocaleDateString("fr-FR", opts);
  return `${d} → ${f}`;
};

export default function VeilleDetail() {
  const { id } = useParams();
  const history = useHistory();
  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");
  const { getVeille, pauseVeille, loading } = useVeilles();
  const { org, profile } = useSession();
  const v = getVeille(id);

  // Données moteur : rapports publiés + dernières retombées réelles
  const [reports, setReports] = useState([]);
  const [retombees, setRetombees] = useState([]);
  useEffect(() => {
    if (!v || !v.id || v.id.startsWith("local-")) return;
    let actif = true;
    fetchReports(v.id).then((r) => actif && setReports(r));
    fetchMentions(v.id, 15).then((m) => actif && setRetombees(m));
    return () => { actif = false; };
  }, [v && v.id]); // eslint-disable-line

  // Job de génération de matrice : chargé au montage, pollé toutes les 5 s tant qu'il tourne
  const [matriceJob, setMatriceJob] = useState(null);
  useEffect(() => {
    if (!v || !v.id || v.id.startsWith("local-")) return;
    let actif = true;
    let timer = null;
    const tick = async () => {
      const job = await fetchMatriceJob(v.id);
      if (!actif) return;
      setMatriceJob(job);
      if (job && JOB_ACTIF(job.statut)) timer = setTimeout(tick, 5000);
    };
    tick();
    return () => { actif = false; if (timer) clearTimeout(timer); };
  }, [v && v.id]); // eslint-disable-line

  async function genererMatrice() {
    const { jobId, error } = await requestMatrice(v.id);
    if (error) {
      toast({ title: "Génération impossible", description: error.message, status: "error", duration: 4000 });
      return;
    }
    toast({ title: "Génération lancée", description: "Comptez 10 à 20 minutes — la page se met à jour toute seule.", status: "success", duration: 4000 });
    setMatriceJob({ id: jobId, statut: "en_attente" });
    setTimeout(async () => setMatriceJob(await fetchMatriceJob(v.id)), 4000);
  }

  async function telechargerMatrice() {
    const url = await downloadMatrice(matriceJob);
    if (url) window.open(url, "_blank", "noopener");
    else toast({ title: "Téléchargement indisponible", status: "error", duration: 3000 });
  }

  // Édition in-app de la matrice (donnée structurée) + régénération du .docx
  const [editeurOuvert, setEditeurOuvert] = useState(false);
  const [sauvegardeEnCours, setSauvegardeEnCours] = useState(false);
  async function enregistrerMatrice(contenu) {
    setSauvegardeEnCours(true);
    const { error } = await saveMatrice(matriceJob.id, contenu);
    if (error) {
      toast({ title: "Enregistrement impossible", description: error.message, status: "error", duration: 4000 });
      setSauvegardeEnCours(false);
      return;
    }
    await requestRerender(matriceJob.id); // régénère le .docx depuis la matrice éditée
    setEditeurOuvert(false);
    setSauvegardeEnCours(false);
    setMatriceJob((j) => ({ ...j, contenu, statut: "rerender" }));
    toast({ title: "Matrice enregistrée", description: "Le .docx est régénéré (quelques secondes).", status: "success", duration: 4000 });
    setTimeout(async () => setMatriceJob(await fetchMatriceJob(v.id)), 6000);
  }

  // Lancer la veille maintenant : collecte immédiate (le rapport rédigé suivra).
  const [runJob, setRunJob] = useState(null);
  const RUN_ACTIF = (s) => ["en_attente", "collecte", "rapport"].includes(s);
  useEffect(() => {
    if (!v || !v.id || v.id.startsWith("local-")) return;
    let actif = true;
    let timer = null;
    const tick = async () => {
      const j = await fetchRunJob(v.id);
      if (!actif) return;
      setRunJob(j);
      if (j && RUN_ACTIF(j.statut)) timer = setTimeout(tick, 4000);
      else if (j && j.statut === "termine") fetchReports(v.id).then((r) => actif && setReports(r));
    };
    tick();
    return () => { actif = false; if (timer) clearTimeout(timer); };
  }, [v && v.id]); // eslint-disable-line
  async function lancerMaintenant() {
    const { jobId, error } = await requestRun(v.id);
    if (error) {
      toast({ title: "Lancement impossible", description: error.message, status: "error", duration: 4000 });
      return;
    }
    toast({ title: "Veille lancée", description: "Collecte en cours — les retombées vont apparaître ici.", status: "success", duration: 4000 });
    setRunJob({ id: jobId, statut: "en_attente" });
    // Polling auto-relancé jusqu'à la fin du run (sinon le spinner reste bloqué).
    const poll = async () => {
      const j = await fetchRunJob(v.id);
      setRunJob(j);
      if (j && RUN_ACTIF(j.statut)) setTimeout(poll, 3000);
      else if (j && j.statut === "termine") fetchReports(v.id).then(setReports);
    };
    setTimeout(poll, 2500);
  }

  // Aperçu du document de cadrage (jsPDF, généré côté client)
  const pdfUrl = useMemo(() => (v ? apercuUrlVeille(v) : null), [v]);
  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  async function telecharger(report) {
    const url = await downloadReport(report, profile && profile.id, org && org.id);
    if (url) {
      window.open(url, "_blank", "noopener");
    } else {
      toast({ title: "Téléchargement indisponible", description: "Impossible de générer le lien signé.", status: "error", duration: 3500 });
    }
  }

  function arreter() {
    if (!v) return;
    const enPause = v.statut === "en_pause";
    pauseVeille(v.id);
    toast({ title: enPause ? "Veille relancée" : "Veille mise en pause", status: enPause ? "success" : "info", duration: 2500 });
  }

  if (loading) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} justify="center" align="center" py="60px">
        <Spinner color="teal.300" size="lg" />
      </Flex>
    );
  }
  if (!v) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} align="center">
        <Text color={textColor} fontWeight="bold">Veille introuvable.</Text>
        <Button mt="16px" onClick={() => history.push("/admin/veilles")}>Retour aux veilles</Button>
      </Flex>
    );
  }

  const aRapport = reports.length > 0;
  const dernierRapport = reports[0];

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Flex align="center" mb="12px" color="gray.400" fontSize="sm" fontWeight="bold" cursor="pointer" w="fit-content" onClick={() => history.push("/admin/veilles")}>
        <Icon as={FaArrowLeft} me="8px" /> Retour aux veilles
      </Flex>

      <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} mb="28px" gap="16px">
        <Flex align="center">
          <Box w="7px" h="60px" borderRadius="full" bg="teal.300" me="18px" flexShrink={0} />
          <Box>
            <Flex align="center" gap="10px" wrap="wrap">
              <Text fontSize={{ base: "2xl", md: "3xl" }} color={textColor} fontWeight="bold" lineHeight="1.1">{v.nom}</Text>
              <Badge colorScheme={statut(v.statut).color} borderRadius="8px">{statut(v.statut).label}</Badge>
            </Flex>
            <Text fontSize="md" color="gray.700" mt="4px">
              {v.secteur}
              {v.hasRealData
                ? ` · ${fmtNombre(v.mentions)} mentions · ${fmtNombre(v.sources)} sources · dernière mention : ${v.derniereDate}`
                : ` · Responsable : ${v.responsable} · Prochaine échéance : ${v.prochaineDate}`}
            </Text>
          </Box>
        </Flex>
        <Flex gap="12px" wrap="wrap" justify={{ base: "flex-start", md: "flex-end" }} align="flex-start">
          <Button variant="outline" borderColor="gray.300" color="gray.700" borderRadius="12px" size="lg" leftIcon={<Icon as={FaPen} fontSize="12px" />} _hover={{ bg: "gray.50" }} onClick={() => history.push(`/admin/nouvelle-veille?edit=${v.id}`)}>Modifier</Button>
          <Button size="lg" bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" leftIcon={<Icon as={FaBolt} fontSize="12px" />} isLoading={runJob && RUN_ACTIF(runJob.statut)} loadingText={runJob && runJob.statut === "collecte" ? "Collecte…" : "Lancement…"} onClick={lancerMaintenant}>Lancer maintenant</Button>
          {(v.statut === "active" || v.statut === "en_pause") && (
            <Button
              variant="outline"
              borderRadius="12px"
              size="lg"
              leftIcon={<Icon as={v.statut === "en_pause" ? FaPlay : FaPause} fontSize="12px" />}
              borderColor={v.statut === "en_pause" ? "teal.300" : "red.300"}
              color={v.statut === "en_pause" ? "teal.600" : "red.500"}
              _hover={{ bg: v.statut === "en_pause" ? "teal.50" : "red.50" }}
              onClick={arreter}
            >
              {v.statut === "en_pause" ? "Reprendre" : "Arrêter"}
            </Button>
          )}
          {aRapport ? (
            <Button size="lg" bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" boxShadow="0 12px 26px rgba(49,151,149,0.4)" leftIcon={<Icon as={FaDownload} />} onClick={() => telecharger(dernierRapport)}>Dernier rapport</Button>
          ) : (
            <Button size="lg" variant="outline" borderColor="teal.300" color="teal.600" borderRadius="12px" leftIcon={<Icon as={FaFilePdf} />} _hover={{ bg: "teal.50" }} onClick={() => telechargerDocVeille(v)}>Document de cadrage</Button>
          )}
        </Flex>
      </Flex>

      {/* Matrice de veille : génération, progression et téléchargement */}
      <Card mb="24px">
        <CardBody p={{ base: "16px", md: "22px" }}>
          <Flex direction="column" w="100%">
            <Flex align="center" justify="space-between" wrap="wrap" gap="12px">
              <Flex align="center">
                <IconBox bg="teal.50" w="48px" h="48px" me="14px"><Icon as={FaSitemap} color="teal.500" h="20px" w="20px" /></IconBox>
                <Box>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>Matrice de veille</Text>
                  <Text fontSize="sm" color="gray.600">
                    {!matriceJob && "Le document de cadrage complet : dossiers prioritaires, mots-clés, personnes clés, tags."}
                    {matriceJob && JOB_ACTIF(matriceJob.statut) && "Génération en cours — recherche vérifiée sur le web, comptez 10 à 20 minutes."}
                    {matriceJob && matriceJob.statut === "termine" && "Matrice générée — à relire avant tout envoi client."}
                    {matriceJob && matriceJob.statut === "echec" && "La génération a échoué."}
                  </Text>
                </Box>
              </Flex>
              {!matriceJob && (
                <Button size="md" bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" leftIcon={<Icon as={FaSitemap} fontSize="12px" />} onClick={genererMatrice}>Générer la matrice</Button>
              )}
              {matriceJob && matriceJob.statut === "termine" && (
                <Flex gap="10px" wrap="wrap">
                  {matriceJob.contenu && (
                    <Button size="md" variant="outline" borderColor="teal.300" color="teal.600" borderRadius="12px" _hover={{ bg: "teal.50" }} leftIcon={<Icon as={FaPen} fontSize="11px" />} onClick={() => setEditeurOuvert(true)}>Voir / éditer</Button>
                  )}
                  <Button size="md" variant="outline" borderColor="teal.300" color="teal.600" borderRadius="12px" _hover={{ bg: "teal.50" }} onClick={genererMatrice}>Régénérer</Button>
                  <Button size="md" bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" leftIcon={<Icon as={FaDownload} fontSize="12px" />} onClick={telechargerMatrice}>Télécharger (.docx)</Button>
                </Flex>
              )}
              {matriceJob && matriceJob.statut === "echec" && (
                <Button size="md" variant="outline" borderColor="red.300" color="red.500" borderRadius="12px" _hover={{ bg: "red.50" }} onClick={genererMatrice}>Réessayer</Button>
              )}
            </Flex>

            {matriceJob && JOB_ACTIF(matriceJob.statut) && (
              <Flex direction="column" gap="8px" mt="18px" p="16px" bg="gray.50" borderRadius="14px">
                {PHASES_MATRICE.map((ph, i) => {
                  const idx = PHASES_MATRICE.findIndex((x) => x.id === matriceJob.statut);
                  const done = i < idx;
                  const current = i === idx;
                  return (
                    <Flex key={ph.id} align="center" gap="10px">
                      {done ? (
                        <Flex w="22px" h="22px" borderRadius="full" bg="teal.300" align="center" justify="center" flexShrink={0}><Icon as={FaCheck} color="white" fontSize="10px" /></Flex>
                      ) : current ? (
                        <Spinner size="sm" color="teal.400" flexShrink={0} />
                      ) : (
                        <Box w="22px" h="22px" borderRadius="full" border="2px solid" borderColor="gray.300" flexShrink={0} />
                      )}
                      <Text fontSize="sm" fontWeight={current ? "bold" : "normal"} color={done || current ? textColor : "gray.500"}>{ph.label}</Text>
                      {current && matriceJob.progression && (
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>· {matriceJob.progression}</Text>
                      )}
                    </Flex>
                  );
                })}
              </Flex>
            )}
            {matriceJob && matriceJob.statut === "echec" && matriceJob.erreur && (
              <Flex align="center" gap="8px" mt="14px" p="12px" bg="red.50" borderRadius="12px">
                <Icon as={FaExclamationTriangle} color="red.400" />
                <Text fontSize="sm" color="red.600" noOfLines={2}>{matriceJob.erreur}</Text>
              </Flex>
            )}
          </Flex>
        </CardBody>
      </Card>

      {/* Exécution de la veille : progression du run à la demande */}
      {runJob && (
        <Card mb="24px">
          <CardBody p={{ base: "18px", md: "26px" }}>
            <Flex direction="column" w="100%">
              <Flex align="center" mb="20px">
                <IconBox bg="teal.50" w="48px" h="48px" me="16px" flexShrink={0}><Icon as={FaBolt} color="teal.500" h="20px" w="20px" /></IconBox>
                <Box>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>Exécution de la veille</Text>
                  <Text fontSize="sm" color="gray.600">
                    {RUN_ACTIF(runJob.statut) && "En cours — collecte des retombées, puis rédaction du rapport."}
                    {runJob.statut === "termine" && "Dernière exécution terminée."}
                    {runJob.statut === "echec" && "L'exécution a échoué."}
                  </Text>
                </Box>
              </Flex>

              <Flex direction="column" gap="18px" p={{ base: "18px", md: "24px" }} bg="gray.50" borderRadius="16px">
                {PHASES_RUN.map((ph) => {
                  let etat;
                  if (ph.id === "collecte") {
                    etat = ["rapport", "termine"].includes(runJob.statut) ? "done" : "current";
                  } else {
                    etat = runJob.statut === "termine" ? "done" : runJob.statut === "rapport" ? "current" : "todo";
                  }
                  const actif = etat === "current";
                  return (
                    <Flex key={ph.id} align="flex-start" gap="14px">
                      <Flex w="24px" justify="center" pt="1px" flexShrink={0}>
                        {etat === "done" ? (
                          <Flex w="24px" h="24px" borderRadius="full" bg="teal.300" align="center" justify="center"><Icon as={FaCheck} color="white" fontSize="11px" /></Flex>
                        ) : actif ? (
                          <Spinner size="sm" color="teal.400" />
                        ) : (
                          <Box w="24px" h="24px" borderRadius="full" border="2px solid" borderColor="gray.300" />
                        )}
                      </Flex>
                      <Box flex="1">
                        <Text fontSize="sm" fontWeight={actif ? "700" : "600"} color={etat === "todo" ? "gray.400" : textColor}>{ph.label}</Text>
                        {actif && runJob.progression && (
                          <Text fontSize="xs" color="gray.500" mt="3px" lineHeight="1.5">{runJob.progression}</Text>
                        )}
                      </Box>
                    </Flex>
                  );
                })}
              </Flex>

              {runJob.statut === "termine" && runJob.progression && (
                <Flex align="center" gap="10px" mt="18px">
                  <Icon as={FaCheck} color="teal.400" fontSize="13px" flexShrink={0} />
                  <Text fontSize="sm" color="gray.600" lineHeight="1.5">{runJob.progression}</Text>
                </Flex>
              )}
              {runJob.statut === "echec" && (
                <Flex align="center" gap="10px" mt="18px" p="16px" bg="red.50" borderRadius="14px">
                  <Icon as={FaExclamationTriangle} color="red.400" flexShrink={0} />
                  <Text fontSize="sm" color="red.600" flex="1" noOfLines={2}>{runJob.erreur || "Erreur inconnue."}</Text>
                  <Button size="sm" variant="outline" borderColor="red.300" color="red.500" borderRadius="10px" flexShrink={0} _hover={{ bg: "red.100" }} onClick={lancerMaintenant}>Réessayer</Button>
                </Flex>
              )}
            </Flex>
          </CardBody>
        </Card>
      )}

      {/* Rapports produits par le moteur de veille */}
      <Card mb="24px">
        <CardBody p={{ base: "16px", md: "22px" }}>
          <Flex direction="column" w="100%">
            <Flex align="center" mb="18px">
              <IconBox bg="teal.50" w="48px" h="48px" me="14px"><Icon as={aRapport ? FaFileWord : FaRegClock} color="teal.500" h="20px" w="20px" /></IconBox>
              <Box>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>Rapports de veille</Text>
                <Text fontSize="sm" color="gray.600">{aRapport ? `${reports.length} rapport${reports.length > 1 ? "s" : ""} produit${reports.length > 1 ? "s" : ""} par le moteur.` : "Disponible après la première exécution de la veille."}</Text>
              </Box>
            </Flex>
            {aRapport ? (
              <Flex direction="column" gap="10px">
                {reports.map((r) => (
                  <Flex key={r.id} align="center" justify="space-between" p="14px 16px" border="1px solid" borderColor="gray.200" borderRadius="14px" _hover={{ borderColor: "teal.200", bg: "teal.50" }} gap="12px" wrap="wrap">
                    <Flex align="center" gap="12px" minW="0">
                      <Icon as={FaFileWord} color="teal.400" fontSize="18px" flexShrink={0} />
                      <Box minW="0">
                        <Text fontWeight="bold" color={textColor} fontSize="sm" noOfLines={1}>{r.titre}</Text>
                        <Text fontSize="xs" color="gray.500">{fmtPeriode(r)} · {Math.round((r.taille_octets || 0) / 1024)} Ko</Text>
                      </Box>
                    </Flex>
                    <Flex align="center" gap="10px">
                      <Badge colorScheme="teal" borderRadius="6px" textTransform="uppercase">{r.fichier_format}</Badge>
                      <Button size="sm" variant="outline" borderColor="teal.300" color="teal.600" borderRadius="10px" leftIcon={<Icon as={FaDownload} fontSize="11px" />} _hover={{ bg: "white" }} onClick={() => telecharger(r)}>Télécharger</Button>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            ) : (
              <Flex direction="column" align="center" justify="center" textAlign="center" py="50px" px="20px" border="1px dashed" borderColor="gray.200" borderRadius="14px" bg="gray.50">
                <Flex w="66px" h="66px" borderRadius="20px" bg="teal.50" align="center" justify="center" mb="16px"><Icon as={FaRegClock} color="teal.400" fontSize="22px" /></Flex>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>Pas encore de rapport</Text>
                <Text fontSize="sm" color="gray.600" mt="6px" maxW="480px">
                  Cette veille est programmée. Le premier rapport apparaîtra ici dès que la veille aura été exécutée{v.prochaineDate && v.prochaineDate !== "—" ? ` — prochaine échéance : ${v.prochaineDate}` : ""}.
                </Text>
              </Flex>
            )}
          </Flex>
        </CardBody>
      </Card>

      {/* Dernières retombées réelles (mentions ingérées par le moteur) */}
      {retombees.length > 0 && (
        <Card>
          <CardBody p={{ base: "16px", md: "22px" }}>
            <Flex direction="column" w="100%">
              <Flex align="center" mb="18px">
                <IconBox bg="teal.50" w="48px" h="48px" me="14px"><Icon as={FaRegNewspaper} color="teal.500" h="20px" w="20px" /></IconBox>
                <Box>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>Dernières retombées</Text>
                  <Text fontSize="sm" color="gray.600">Les {retombees.length} mentions les plus récentes collectées par le moteur.</Text>
                </Box>
              </Flex>
              <Flex direction="column">
                {retombees.map((m) => (
                  <Flex key={m.id} align="center" justify="space-between" py="12px" borderBottom="1px solid" borderColor="gray.100" gap="12px" _last={{ borderBottom: "none" }}>
                    <Box minW="0">
                      <Link href={m.url || undefined} isExternal _hover={{ color: "teal.500" }}>
                        <Text fontWeight="bold" color={textColor} fontSize="sm" noOfLines={1}>{m.titre}</Text>
                      </Link>
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>{m.source} · {canal(m.canal).label} · {m.date}</Text>
                    </Box>
                    <Badge colorScheme={tonalite(m.tonalite).color} borderRadius="8px" flexShrink={0}>{tonalite(m.tonalite).label}</Badge>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          </CardBody>
        </Card>
      )}

      <MatriceEditor
        isOpen={editeurOuvert}
        onClose={() => setEditeurOuvert(false)}
        contenu={matriceJob && matriceJob.contenu}
        onSave={enregistrerMatrice}
        saving={sauvegardeEnCours}
      />
    </Flex>
  );
}
